import { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, MessagesSquare, LogOut, Lock } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { CorrespondencePanel } from "../components/CorrespondencePanel";
import { api } from "../lib/api";

export default function CorrespondencePortal() {
  const { id: orgId } = useParams();
  const [brand, setBrand] = useState({ name: "", has_logo: false });
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const check = useCallback(async () => {
    try {
      const me = await api.me();
      if (me && me.role === "supervisor") setUser(me); else setUser(null);
    } catch { setUser(null); }
    finally { setChecking(false); }
  }, []);

  useEffect(() => {
    api.publicOrgBranding(orgId).then(setBrand).catch(() => setBrand({ name: "المراسلات", has_logo: false }));
    check();
  }, [orgId, check]);

  const login = async () => {
    if (!email.trim() || !password) { toast.warning("أدخل البريد وكلمة المرور"); return; }
    setBusy(true);
    try {
      await api.corrPortalLogin(orgId, email.trim(), password);
      await check();
      toast.success("مرحباً بك");
    } catch (e) { toast.error(e?.response?.data?.detail || "تعذّر الدخول"); }
    finally { setBusy(false); }
  };

  const logout = () => { document.cookie = "session_token=; Max-Age=0; path=/"; setUser(null); setPassword(""); };

  const Logo = () => (brand.has_logo
    ? <img src={api.publicOrgLogoUrl(orgId)} alt={brand.name} className="h-12 w-12 rounded-xl object-contain bg-white border border-slate-200" />
    : <div className="h-12 w-12 rounded-xl bg-blue-600 grid place-items-center text-white"><MessagesSquare className="h-6 w-6" /></div>);

  if (checking) {
    return <div className="min-h-screen grid place-items-center bg-slate-50"><Loader2 className="h-7 w-7 animate-spin text-blue-600" /></div>;
  }

  if (!user) {
    return (
      <div dir="rtl" className="min-h-screen grid place-items-center bg-gradient-to-br from-slate-100 to-blue-50 p-4 font-body">
        <div className="w-full max-w-sm bg-white rounded-2xl shadow-xl border border-slate-100 p-6 space-y-5" data-testid="corr-portal-login">
          <div className="flex flex-col items-center gap-3 text-center">
            <Logo />
            <div>
              <h1 className="font-heading text-xl font-bold text-slate-800">{brand.name || "بوابة المراسلات"}</h1>
              <p className="text-xs text-slate-400 mt-0.5">بوابة المراسلات الإدارية</p>
            </div>
          </div>
          <div className="space-y-3">
            <div className="space-y-1">
              <label className="text-xs font-semibold text-slate-500">البريد الإلكتروني</label>
              <Input data-testid="corr-portal-email" dir="ltr" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="name@example.com" />
            </div>
            <div className="space-y-1">
              <label className="text-xs font-semibold text-slate-500">كلمة المرور</label>
              <Input data-testid="corr-portal-password" dir="ltr" type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") login(); }} placeholder="••••••••" />
            </div>
            <Button onClick={login} disabled={busy} className="w-full gap-1.5" data-testid="corr-portal-submit">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Lock className="h-4 w-4" />} دخول
            </Button>
          </div>
          <p className="text-[11px] text-slate-400 text-center">هذه بوابة مخصّصة للمراسلات فقط. للحصول على بيانات الدخول تواصل مع الإدارة.</p>
        </div>
      </div>
    );
  }

  return (
    <div dir="rtl" className="min-h-screen bg-slate-50 font-body">
      <header className="sticky top-0 z-20 bg-white border-b border-slate-200 px-4 h-14 flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          {brand.has_logo
            ? <img src={api.publicOrgLogoUrl(orgId)} alt={brand.name} className="h-8 w-8 rounded-lg object-contain border border-slate-200" />
            : <MessagesSquare className="h-6 w-6 text-blue-600" />}
          <div className="leading-tight">
            <p className="font-heading font-bold text-sm text-slate-800">{brand.name || "المراسلات"}</p>
            <p className="text-[10px] text-slate-400">بوابة المراسلات — {user.name}</p>
          </div>
        </div>
        <Button variant="ghost" size="sm" onClick={logout} data-testid="corr-portal-logout" className="gap-1.5 text-slate-500">
          <LogOut className="h-4 w-4" /> خروج
        </Button>
      </header>
      <main className="max-w-5xl mx-auto p-3 sm:p-5">
        <CorrespondencePanel user={user} onUnreadChange={() => {}} />
      </main>
    </div>
  );
}
